/**
 * Conversão dos pisos do wizard para a unidade em que a tela os exibe.
 *
 * Porta `cteatf_valor.dart` (App Tresco). A regra: comparar sempre na
 * precisão que o campo exibe — o piso nasce de `km × tarifa` (double), mas o
 * campo de valor trabalha em centavos inteiros e o de KM sem casas decimais.
 * Comparar contra a base crua recusava o próprio número que a tela mostrava.
 */

/** Tolerância para ruído de ponto flutuante, em centavos. */
const EPSILON_CENTAVO = 1e-6;

/** Piso em centavos inteiros — teto, nunca arredondamento para baixo. */
export function centavosPiso(reais: number): number {
  return reais <= 0 ? 0 : Math.ceil(reais * 100 - EPSILON_CENTAVO);
}

/** Piso de KM na precisão que o campo exibe (sem casas decimais). */
export function kmPiso(km: number): number {
  return km <= 0 ? 0 : Math.round(km);
}

/**
 * O valor (em centavos) com que o passo de valor abre.
 * Campo intocado (`atual === 0`) abre na sugestão da tarifa; já editado, só
 * o piso manda — nunca eleva em silêncio um número que o cooperado escolheu.
 */
export function valorAoAbrirOPasso({ atual, sugestao, piso }: { atual: number; sugestao: number; piso: number }): number {
  if (atual === 0) return sugestao > 0 ? sugestao : atual;
  return atual < piso ? piso : atual;
}

/** KM faturável — vira dinheiro e vira documento fiscal. */
export function kmFaturavelDe({
  kmIda, kmVolta, somenteIda, kmFiscalIncluiVolta,
}: { kmIda: number; kmVolta: number; somenteIda: boolean; kmFiscalIncluiVolta: boolean }): number {
  return somenteIda ? kmIda : (kmFiscalIncluiVolta ? kmIda + kmVolta : kmIda);
}

/**
 * Custo previsto da emissão para quem paga por fatura: `vTPrest × alíquota%`.
 * `null` (nunca zero) quando falta a alíquota ou o vTPrest — "R$ 0,00" com
 * cara de certo é pior que admitir que não sabemos (mesma escolha do backend).
 */
export function custoPorFatura({ vTPrest, aliquotaIcms }: { vTPrest: number; aliquotaIcms?: number | null }): number | null {
  const a = aliquotaIcms;
  if (a == null || a <= 0 || a > 100) return null;
  if (vTPrest <= 0) return null;
  return (vTPrest * a) / 100;
}
